import { useState, type ReactNode, type RefObject } from 'react';
import { ArrowLeft, ArrowUpDown, ChevronDown, ListFilter } from 'lucide-react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { filterChipVariants } from '@voll-intelligence/ui-kit/ui/filter-chip';
import { BottomSheet } from '../bottom_sheet.js';

/**
 * A barra de CHIPS de filtro e ordenação do celular (#3140): a fila que o host
 * monta sob a pílula de busca, no slot da top bar. Dois chips fixos — `Filtros`
 * (com o contador, abre o painel completo) e a ordenação (abre a folha de
 * ordenar) — e um chip por eixo de corte: os de liga/desliga viram na hora
 * (`toggle`), os de valor abrem uma folha parcial com rascunho (`sheet`).
 *
 * A ORDEM é a do painel completo, com uma regra por cima: eixo ATIVO passa à
 * frente dos inativos, e o que só existe ativo (as comodidades) some quando
 * inativo (`hiddenWhenInactive`).
 *
 * Só primitivo do DS + utilitária. O produto monta os chips; aqui é a fila, a
 * folha parcial e a folha de ordenar.
 */
export type ResultsFilterChip = {
  readonly id: string;
  readonly label: string;
  /** Corte em pé neste eixo — vem antes dos inativos e ganha a tinta forte. */
  readonly active: boolean;
  /** `filters`/`sort`: os dois fixos, com ícone; `toggle`: o toque vira o corte
   *  na hora (`aria-pressed`); `sheet`: abre uma folha (caret). */
  readonly kind: 'filters' | 'sort' | 'toggle' | 'sheet';
  /** O contador do `Filtros`. */
  readonly badge?: number;
  /** Só aparece quando ativo (as comodidades do hotel, o número do voo). */
  readonly hiddenWhenInactive?: boolean;
  readonly onPress: () => void;
};

/** Os fixos primeiro, depois os ativos na ordem do painel, depois os inativos
 *  (menos os que só existem ativos). */
function orderChips(chips: readonly ResultsFilterChip[]): ResultsFilterChip[] {
  const fixed = chips.filter((chip) => chip.kind === 'filters' || chip.kind === 'sort');
  const axes = chips.filter((chip) => chip.kind !== 'filters' && chip.kind !== 'sort');
  return [
    ...fixed,
    ...axes.filter((chip) => chip.active),
    ...axes.filter((chip) => !chip.active && !chip.hiddenWhenInactive),
  ];
}

function chipIcon(kind: ResultsFilterChip['kind']): ReactNode {
  if (kind === 'filters') return <ListFilter className="size-4" aria-hidden="true" />;
  if (kind === 'sort') return <ArrowUpDown className="size-4" aria-hidden="true" />;
  return null;
}

export function ResultsFilterChips({
  chips,
  label,
  disabled = false,
  collapsed = false,
  chipRefs,
}: {
  readonly chips: readonly ResultsFilterChip[];
  /** O nome acessível da fila ("Filtros e ordenação"). */
  readonly label: string;
  readonly disabled?: boolean;
  /** A fila recolhida (#3274): sobe para fora da vista enquanto a lista rola
   *  para baixo e volta quando ela rola para cima. Só a fila se move — o slot
   *  do host que a monta lê o `data-collapsed` para encolher a própria altura.
   *  `inert` enquanto recolhida: um chip invisível não pode receber foco. */
  readonly collapsed?: boolean;
  /** Os botões por id — quem abre uma folha devolve o foco ao chip que a abriu. */
  readonly chipRefs?: RefObject<Map<string, HTMLButtonElement>>;
}): ReactNode {
  return (
    <div
      role="toolbar"
      aria-label={label}
      data-collapsed={collapsed ? '' : undefined}
      inert={collapsed}
      // Recolhe em 200ms e volta em 160ms (`ease-out`), como o spec; sem
      // transição sob `prefers-reduced-motion`.
      className="flex gap-2 overflow-x-auto px-4 py-2 transition-[translate,opacity] duration-[160ms] ease-out [scrollbar-width:none] motion-reduce:transition-none data-collapsed:-translate-y-full data-collapsed:opacity-0 data-collapsed:duration-200 [&::-webkit-scrollbar]:hidden"
    >
      {orderChips(chips).map((chip) => (
        // The DS chip look: a toggle carries `aria-pressed`; a chip that opens a sheet is «on»
        // through `data-active` while its axis holds a cut.
        <button
          key={chip.id}
          ref={(element) => {
            if (!chipRefs) return;
            if (element) chipRefs.current.set(chip.id, element);
            else chipRefs.current.delete(chip.id);
          }}
          type="button"
          className={filterChipVariants()}
          data-active={chip.kind === 'toggle' ? undefined : chip.active}
          aria-pressed={chip.kind === 'toggle' ? chip.active : undefined}
          disabled={disabled}
          onClick={chip.onPress}
        >
          {chipIcon(chip.kind)}
          <span>{chip.label}</span>
          {chip.badge !== undefined && chip.badge > 0 && (
            <span className="bg-foreground text-background inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] leading-none">
              {chip.badge}
            </span>
          )}
          {chip.kind === 'sheet' && <ChevronDown className="size-4" aria-hidden="true" />}
        </button>
      ))}
    </div>
  );
}

/** O cabeçalho das folhas dos chips: ← que fecha e o título centrado. */
function ChipSheetHeader({
  title,
  backLabel,
  onBack,
}: {
  readonly title: string;
  readonly backLabel: string;
  readonly onBack: () => void;
}): ReactNode {
  return (
    <div className="flex items-center gap-2 px-2 pb-2">
      <Button type="button" variant="ghost" size="icon-sm" aria-label={backLabel} onClick={onBack}>
        <ArrowLeft aria-hidden="true" />
      </Button>
      <h2 className="m-0 min-w-0 flex-1 truncate text-center text-base font-semibold">{title}</h2>
      <span className="size-8 shrink-0" aria-hidden="true" />
    </div>
  );
}

/** O rascunho da folha parcial: nasce do valor aplicado a cada abertura. */
function useDraft<Value>(open: boolean, value: Value): [Value, (next: Value) => void] {
  const [draft, setDraft] = useState(value);
  const [seenOpen, setSeenOpen] = useState(open);
  if (open !== seenOpen) {
    setSeenOpen(open);
    if (open) setDraft(value);
  }
  return [draft, setDraft];
}

/**
 * A folha PARCIAL de um eixo (o "filtro rápido" do protótipo): ← e título,
 * o corpo do produto sobre um rascunho, e `Redefinir` + `Aplicar` no pé. Só
 * o Aplicar escreve; fechar descarta.
 */
export function ResultsChipSheet<Value>({
  open,
  onOpenChange,
  returnFocusTo,
  title,
  backLabel,
  resetLabel,
  applyLabel,
  value,
  emptyValue,
  locked = false,
  onApply,
  children,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly returnFocusTo: RefObject<HTMLElement | null>;
  readonly title: string;
  readonly backLabel: string;
  readonly resetLabel: string;
  readonly applyLabel: string;
  /** O corte APLICADO deste eixo — a semente do rascunho a cada abertura. */
  readonly value: Value;
  /** O eixo zerado — o que o `Redefinir` põe no rascunho. */
  readonly emptyValue: Value;
  readonly locked?: boolean;
  readonly onApply: (value: Value) => void;
  readonly children: (draft: Value, setDraft: (next: Value) => void) => ReactNode;
}): ReactNode {
  const [draft, setDraft] = useDraft(open, value);
  const close = () => {
    onOpenChange(false);
  };
  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      returnFocusTo={returnFocusTo}
      label={title}
    >
      <ChipSheetHeader title={title} backLabel={backLabel} onBack={close} />
      <div className="px-4 pb-4">{children(draft, setDraft)}</div>
      <div className="flex gap-2 px-4 pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))]">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="flex-1 rounded-full"
          disabled={locked}
          onClick={() => {
            setDraft(emptyValue);
          }}
        >
          {resetLabel}
        </Button>
        <Button
          type="button"
          size="lg"
          className="flex-1 rounded-full"
          disabled={locked}
          onClick={() => {
            onApply(draft);
            close();
          }}
        >
          {applyLabel}
        </Button>
      </div>
    </BottomSheet>
  );
}

/** A folha de ORDENAR: uma lista de rádio; escolher aplica e fecha. */
export function ResultsSortSheet<Order extends string>({
  open,
  onOpenChange,
  returnFocusTo,
  title,
  backLabel,
  orders,
  value,
  orderLabel,
  locked = false,
  onChange,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly returnFocusTo: RefObject<HTMLElement | null>;
  readonly title: string;
  readonly backLabel: string;
  readonly orders: readonly Order[];
  readonly value: Order;
  readonly orderLabel: (order: Order) => string;
  readonly locked?: boolean;
  readonly onChange: (order: Order) => void;
}): ReactNode {
  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      returnFocusTo={returnFocusTo}
      label={title}
    >
      <ChipSheetHeader
        title={title}
        backLabel={backLabel}
        onBack={() => {
          onOpenChange(false);
        }}
      />
      <div
        role="radiogroup"
        aria-label={title}
        className="flex flex-col pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))]"
      >
        {orders.map((order) => {
          const checked = order === value;
          return (
            <button
              key={order}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={locked}
              className="flex items-center gap-3 px-5 py-3 text-left text-sm disabled:opacity-50"
              onClick={() => {
                onChange(order);
                onOpenChange(false);
              }}
            >
              <span
                aria-hidden="true"
                className="border-border size-4 shrink-0 rounded-full border data-[on=true]:border-[5px] data-[on=true]:border-foreground"
                data-on={checked ? 'true' : undefined}
              />
              {orderLabel(order)}
            </button>
          );
        })}
      </div>
    </BottomSheet>
  );
}
